// src/screens/admin/VuelosConfiguracion.jsx
// Sub-sección C del panel "Vuelos Guyana": tasa BRL específica de Vuelos
// (separada de la de Cambio de Divisas) y margen/porcentaje por proveedor.
// Cambiar estos valores solo afecta rutas futuras -- las ya publicadas
// tienen su precio_zelle fijado al momento de publicar.
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import {
  getTasaVuelos, actualizarTasaVuelos, getTasaEurVuelos, actualizarTasaEurVuelos,
  getTasaMxnVuelos, actualizarTasaMxnVuelos, actualizarProveedorVuelos,
} from '../../services/vuelosGuyanaService';

function ProveedorRow({ proveedor, onSaved }) {
  const [margen, setMargen] = useState(proveedor.margen_usd_default);
  const [porcentaje, setPorcentaje] = useState(proveedor.porcentaje_usd_cuba);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!margen || parseFloat(margen) < 0) return toast('Margen inválido.', 'error');
    if (!porcentaje || parseFloat(porcentaje) < 0) return toast('Porcentaje inválido.', 'error');
    setSaving(true);
    try {
      await actualizarProveedorVuelos(proveedor.id, margen, porcentaje);
      toast(`${proveedor.nombre} actualizado ✅`);
      onSaved();
    } catch { toast('Error al guardar.', 'error'); }
    setSaving(false);
  };

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 10 }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 10 }}>{proveedor.nombre}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div><label>Margen USD</label><input type="number" value={margen} onChange={(e) => setMargen(e.target.value)} inputMode="decimal" /></div>
        <div><label>% USD Cuba</label><input type="number" value={porcentaje} onChange={(e) => setPorcentaje(e.target.value)} inputMode="decimal" /></div>
      </div>
      <button onClick={handleSave} disabled={saving} style={{ width: '100%', background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)', borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700 }}>
        {saving ? '...' : '💾 Guardar'}
      </button>
    </div>
  );
}

export default function VuelosConfiguracion({ proveedores, onProveedoresChanged }) {
  const [tasa, setTasa] = useState('');
  const [savingTasa, setSavingTasa] = useState(false);
  const [tasaEur, setTasaEur] = useState('');
  const [savingTasaEur, setSavingTasaEur] = useState(false);
  const [tasaMxn, setTasaMxn] = useState('');
  const [savingTasaMxn, setSavingTasaMxn] = useState(false);

  useEffect(() => {
    getTasaVuelos().then((t) => setTasa(t.toString()));
    getTasaEurVuelos().then((t) => setTasaEur(t.toString()));
    getTasaMxnVuelos().then((t) => setTasaMxn(t.toString()));
  }, []);

  const handleSaveTasa = async () => {
    if (!tasa || parseFloat(tasa) <= 0) return toast('Tasa inválida.', 'error');
    setSavingTasa(true);
    try {
      await actualizarTasaVuelos(tasa);
      toast('Tasa BRL de Vuelos actualizada ✅');
    } catch { toast('Error al guardar.', 'error'); }
    setSavingTasa(false);
  };

  const handleSaveTasaEur = async () => {
    if (!tasaEur || parseFloat(tasaEur) <= 0) return toast('Tasa inválida.', 'error');
    setSavingTasaEur(true);
    try {
      await actualizarTasaEurVuelos(tasaEur);
      toast('Tasa EUR de Vuelos actualizada ✅');
    } catch { toast('Error al guardar.', 'error'); }
    setSavingTasaEur(false);
  };

  const handleSaveTasaMxn = async () => {
    if (!tasaMxn || parseFloat(tasaMxn) <= 0) return toast('Tasa inválida.', 'error');
    setSavingTasaMxn(true);
    try {
      await actualizarTasaMxnVuelos(tasaMxn);
      toast('Tasa MXN de Vuelos actualizada ✅');
    } catch { toast('Error al guardar.', 'error'); }
    setSavingTasaMxn(false);
  };

  return (
    <div>
      <p className="section-label">TASA BRL — VUELOS</p>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>Independiente de la tasa de Cambio de Divisas.</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input value={tasa} onChange={(e) => setTasa(e.target.value)} inputMode="decimal" style={{ flex: 1 }} />
        <button className="btn-primary" onClick={handleSaveTasa} disabled={savingTasa} style={{ width: 100 }}>
          {savingTasa ? <span className="spinner" /> : 'Guardar'}
        </button>
      </div>

      <p className="section-label">TASA EUR — VUELOS</p>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
        EUR por cada 1 USD. Para protegerte de que el euro se debilite entre el cobro y la conversión real, SUBE
        este número (nunca lo bajes) -- mismo gesto que con la tasa BRL.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input value={tasaEur} onChange={(e) => setTasaEur(e.target.value)} inputMode="decimal" style={{ flex: 1 }} />
        <button className="btn-primary" onClick={handleSaveTasaEur} disabled={savingTasaEur} style={{ width: 100 }}>
          {savingTasaEur ? <span className="spinner" /> : 'Guardar'}
        </button>
      </div>

      <p className="section-label">TASA MXN — VUELOS</p>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
        MXN por cada 1 USD. Mismo gesto que con EUR/BRL: SUBE este número (nunca lo bajes) para protegerte de que el
        peso se debilite entre el cobro y la conversión real.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input value={tasaMxn} onChange={(e) => setTasaMxn(e.target.value)} inputMode="decimal" style={{ flex: 1 }} />
        <button className="btn-primary" onClick={handleSaveTasaMxn} disabled={savingTasaMxn} style={{ width: 100 }}>
          {savingTasaMxn ? <span className="spinner" /> : 'Guardar'}
        </button>
      </div>

      <p className="section-label">PROVEEDORES</p>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Solo afecta rutas futuras -- las ya publicadas conservan su precio Zelle fijado.
      </p>
      {proveedores.map((p) => <ProveedorRow key={p.id} proveedor={p} onSaved={onProveedoresChanged} />)}
    </div>
  );
}
